// PWA Installation and Management Utilities

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let isInstallPromptShown = false;

// Initialize PWA install prompt
export const initPWAInstall = () => {
  // Listen for beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    console.log('PWA install prompt ready');
    
    // Show custom install prompt after 30 seconds if not already shown
    if (!isInstallPromptShown && !isPWAInstalled()) {
      setTimeout(() => {
        showInstallPrompt();
      }, 30000);
    }
  });

  // Listen for app installed event
  window.addEventListener('appinstalled', () => {
    console.log('PWA installed successfully');
    deferredPrompt = null;
    localStorage.setItem('pwa_installed', 'true');
  });
};

// Show install prompt
const showInstallPrompt = () => {
  if (deferredPrompt && !isInstallPromptShown) {
    isInstallPromptShown = true;
    // Trigger custom UI to show install prompt
    window.dispatchEvent(new CustomEvent('show-pwa-install-prompt'));
  }
};

// Install PWA
export const installPWA = async (): Promise<boolean> => {
  if (!deferredPrompt) {
    console.log('Install prompt not available');
    return false;
  }

  try {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted PWA installation');
      deferredPrompt = null;
      return true;
    } else {
      console.log('User dismissed PWA installation');
      return false;
    }
  } catch (error) {
    console.error('Error installing PWA:', error);
    return false;
  }
};

// Check if PWA is installable
export const isPWAInstallable = (): boolean => {
  // Check if already installed
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return false;
  }

  // Check if prompt is available
  if (deferredPrompt) {
    return true;
  }

  // Check if dismissed recently (within 7 days)
  const dismissed = localStorage.getItem('pwa_prompt_dismissed');
  if (dismissed) {
    const dismissedTime = parseInt(dismissed, 10);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - dismissedTime < sevenDays) {
      return false;
    }
  }

  // For desktop browsers, always show option since they might have install button in address bar
  const isDesktop = !(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  if (isDesktop) {
    return true; // Show instructions for desktop users
  }

  return false;
};

// Check if PWA is already installed
export const isPWAInstalled = (): boolean => {
  // Check if running in standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isInstalled = localStorage.getItem('pwa_installed') === 'true';
  
  return isStandalone || isInstalled;
};

// Get platform-specific installation instructions
export const getInstallInstructions = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Desktop Chrome/Edge
  if (!(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent))) {
    return {
      platform: 'Desktop',
      steps: [
        'Look for the install icon (⊕) in the address bar on the right side',
        'Click the install icon and select "Install"',
        'Or click the 3-dot menu (⋮) and select "Install Shaadipariwar..."',
        'The app will open in its own window like a native application',
        'You can find it in your Start Menu/Applications folder'
      ]
    };
  }

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return {
      platform: 'iOS',
      steps: [
        'Tap the Share button at the bottom of Safari',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
        'The app will appear on your home screen'
      ]
    };
  } else if (/android/.test(userAgent)) {
    return {
      platform: 'Android',
      steps: [
        'Tap the menu button (three dots) in Chrome',
        'Select "Add to Home screen" or "Install app"',
        'Tap "Add" or "Install"',
        'The app will appear on your home screen'
      ]
    };
  } else {
    return {
      platform: 'Desktop',
      steps: [
        'Click the install icon in the address bar',
        'Or click the menu (three dots) and select "Install Shaadipariwar"',
        'Click "Install" in the popup',
        'The app will open in its own window'
      ]
    };
  }
};

// Register service worker
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    // Determine the correct service worker path based on environment
    const basePath = import.meta.env.BASE_URL || '/';
    // Ensure proper path formatting with trailing slash
    const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;
    const swPath = `${normalizedBasePath}sw.js`;
    
    console.log('Registering service worker at:', swPath);
    
    const registration = await navigator.serviceWorker.register(swPath, {
      scope: normalizedBasePath,
      updateViaCache: 'none'
    });

    console.log('Service Worker registered successfully:', registration.scope);

    // Check for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker available
            window.dispatchEvent(new CustomEvent('sw-update-available'));
          }
        });
      }
    });

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    // More specific error logging
    if (error instanceof Error) {
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
    }
    // Don't throw error, just return null to allow app to continue
    return null;
  }
};

// Enable background sync
export const enableBackgroundSync = async (tag: string): Promise<boolean> => {
  if (!('serviceWorker' in navigator) || !('sync' in ServiceWorkerRegistration.prototype)) {
    console.log('Background Sync not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register(tag);
    console.log(`Background sync registered: ${tag}`);
    return true;
  } catch (error) {
    console.error('Background sync registration failed:', error);
    return false;
  }
};

// Enable periodic sync
export const enablePeriodicSync = async (tag: string, interval: number): Promise<boolean> => {
  if (!('serviceWorker' in navigator) || !('periodicSync' in ServiceWorkerRegistration.prototype)) {
    console.log('Periodic Sync not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const status = await navigator.permissions.query({
      name: 'periodic-background-sync' as PermissionName,
    });

    if (status.state === 'granted') {
      await (registration as any).periodicSync.register(tag, {
        minInterval: interval,
      });
      console.log(`Periodic sync registered: ${tag}`);
      return true;
    } else {
      console.log('Periodic sync permission not granted');
      return false;
    }
  } catch (error) {
    console.error('Periodic sync registration failed:', error);
    return false;
  }
};

// Request push notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      await subscribeToPushNotifications();
      return true;
    } else {
      console.log('Notification permission denied');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Subscribe to push notifications
const subscribeToPushNotifications = async (): Promise<void> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    
    if (!vapidPublicKey || vapidPublicKey.includes('your_vapid_public_key_here')) {
      console.warn('VAPID public key not configured');
      return;
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    // Send subscription to backend
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    
    if (supabaseUrl) {
      await fetch(`${supabaseUrl}/functions/v1/save-push-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
        }),
      });
      
      console.log('Push subscription saved');
    }
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
  }
};

// Convert VAPID key
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
};

// Check if user is online
export const isOnline = (): boolean => {
  return navigator.onLine;
};

// Initialize online/offline status listener
export const initOnlineStatusListener = (
  onOnline?: () => void,
  onOffline?: () => void
): void => {
  window.addEventListener('online', () => {
    console.log('App is online');
    if (onOnline) onOnline();
  });

  window.addEventListener('offline', () => {
    console.log('App is offline');
    if (onOffline) onOffline();
  });
};

// Cache important data for offline use
export const cacheOfflineData = async (key: string, data: any): Promise<void> => {
  try {
    const cache = await caches.open('offline-data-v1');
    const response = new Response(JSON.stringify(data));
    await cache.put(key, response);
    console.log(`Data cached: ${key}`);
  } catch (error) {
    console.error('Error caching data:', error);
  }
};

// Get cached data
export const getCachedData = async (key: string): Promise<any | null> => {
  try {
    const cache = await caches.open('offline-data-v1');
    const response = await cache.match(key);
    
    if (response) {
      const data = await response.json();
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting cached data:', error);
    return null;
  }
};

// Clear old caches
export const clearOldCaches = async (): Promise<void> => {
  try {
    const cacheNames = await caches.keys();
    const currentCaches = ['offline-data-v1', 'pwa-cache-v1'];
    
    await Promise.all(
      cacheNames.map((cacheName) => {
        if (!currentCaches.includes(cacheName)) {
          console.log(`Deleting old cache: ${cacheName}`);
          return caches.delete(cacheName);
        }
      })
    );
  } catch (error) {
    console.error('Error clearing old caches:', error);
  }
};

// Update service worker
export const updateServiceWorker = async (): Promise<void> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    
    // Skip waiting and activate new service worker
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Reload page to use new service worker
    window.location.reload();
  } catch (error) {
    console.error('Error updating service worker:', error);
  }
};

// Get notification permission status
export const getNotificationPermission = (): NotificationPermission => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
};

// Show local notification
export const showLocalNotification = async (
  title: string,
  options?: NotificationOptions
): Promise<void> => {
  if (getNotificationPermission() !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/pwa-icons/icon-192x192.png',
      badge: '/pwa-icons/icon-72x72.png',
      vibrate: [200, 100, 200],
      ...options,
    });
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};
